/**
 * P2 step 3, the practice quarter (screens §P2, content §2.2): a mini Decide over the practice decisions, then "Finish practice" →
 * `practiceRun` → the practice result. Nothing here is scored or saved; it never reaches P9.
 */
import { useState } from "react";
import { STANDING_POLICY, withDecisionPath, type Decisions, type DecisionFamily } from "@bsim/domain";
import type { ContentCatalog, ErrorBody, PracticeResult } from "@bsim/contracts";
import type { ApiClient } from "../api/client";
import { useCoach } from "../coach/CoachDrawer";
import { GxDecisionControl } from "../decisions/DecisionControl";
import { changeTag, readDecision, sameValue } from "../decisions/decisionValues";
import { PracticeResultView } from "../results";
import { priceCauses } from "../results/PracticeResult";
import { GxLoading } from "../theme/GxLoading";
import { GxStickyBar } from "../theme/GxStickyBar";
import { GxPill } from "../theme/GxPill";
import { GxCard, GxErrorBanner } from "../theme/primitives";
import { TWIN_DEFINITION } from "@gxs/metric-defs";
import { formatUsdPerShare } from "../format";

// content §2.2 step 3: deposit pricing and loan growth editable — the opening focus decisions of these two families.
const PRACTICE_FAMILIES: readonly DecisionFamily[] = ["loans", "deposits"];

/** The twin card's line: the shared definition, then what the score is; the regulator's rule is in the glossary. */
export const TWIN_LINE = `${TWIN_DEFINITION} Your score is how much more shareholder value you create than your twin: share price growth plus capital returned.`;

/**
 * Nudges that name what a decision does (content §2.2 nudges 4–5); any other practice decision gets the generic ask. `when` is the direction the nudge describes: "up" is a move toward the later option in the
 * catalog's list (the catalog's own rule for `deferredNotes.up`), "any" fits either way; a move the other way gets the neutral question.
 */
const DECISION_NUDGES: Readonly<Record<string, { readonly text: string; readonly when: "up" | "any" }>> = {
  "loans.cre.growth": { text: "Grow a notch. Why does provision jump before any loan goes bad?", when: "up" },
  "deposits.retailMm.rate": { text: "Try paying above or below the market rate, then finish the practice. What moved, and why?", when: "any" },
};

/** The coach's prompts for this practice quarter, from the decisions it actually offers (a decision that is not here gets no nudge), then the rationale and the bridge. */
const nudgeOf = (e: { readonly decisionId: string; readonly label: string }, lowered: ReadonlySet<string>): string | undefined => {
  const nudge = DECISION_NUDGES[e.decisionId];
  return nudge === undefined ? undefined : nudge.when === "up" && lowered.has(e.decisionId) ? `You moved ${e.label} the other way. What moved, and why?` : nudge.text;
};
export const practicePrompts = (entries: readonly { readonly decisionId: string; readonly label: string }[], moved: ReadonlySet<string> = new Set(), lowered: ReadonlySet<string> = new Set(), bridgeShown = false): readonly string[] => [
  // A nudge that names what a decision did appears only once that decision has moved, and only in the direction it describes ("Grow a notch" is not said after a cut); a move the other way gets a neutral question.
  ...entries.map((e) => (moved.has(e.decisionId) ? nudgeOf(e, lowered) : undefined) ?? `Try a different setting on ${e.label}, then finish the practice. What moved, and why?`),
  "One sentence: what do you expect, and why?",
  // The coach points only at what is on screen: the bridge after the quarter is run, the setting rows before.
  bridgeShown ? "Why did the net interest margin move? Point at the biggest bar in the bridge." : "Which of these settings do you expect to move the net interest margin most, and why?",
];

/** The practice's price causes as the Coach's first answer: each cause, biggest first, in the result's own words. */
const priceAnswer = (result: PracticeResult, catalog: ContentCatalog, moved: readonly string[]): string => {
  const causes = priceCauses(result, catalog, moved);
  const named = (causes?.rows ?? []).filter((r) => r.usd !== 0).map((r) => `${r.label} (${formatUsdPerShare(r.usd, { signed: true })})`);
  if (named.length === 0) return "Nothing moved the share price against your twin this quarter. A change to hiring takes a year to show, so the year's figures are the ones to read.";
  return `Against your twin, the share price moved because of ${named.join(", then ")}. The first one is the biggest cause.`;
};

/** The practice step's own state, held by the page (a step change remounts the step, and a result must be able to go back to the settings that made it). */
export function usePracticeState() {
  const [decisions, setDecisions] = useState<Decisions>(STANDING_POLICY);
  const [result, setResult] = useState<PracticeResult | null>(null);
  return { decisions, setDecisions, result, setResult };
}
export type PracticeState = ReturnType<typeof usePracticeState>;

/** True when a leg of the decision sits earlier in its option list than the standing policy's value (a cut), the direction a "grow" nudge does not describe. */
const loweredFrom = (entry: ContentCatalog["decisions"][number], now: Decisions): boolean =>
  Object.entries(entry.options).some(([sub, options]) => {
    const [before, after] = [readDecision(STANDING_POLICY, entry.decisionId, sub), readDecision(now, entry.decisionId, sub)];
    if (before === undefined || after === undefined || sameValue(before, after)) return false;
    const [from, to] = [options.findIndex((o) => sameValue(o.value, before)), options.findIndex((o) => sameValue(o.value, after))];
    return from >= 0 && to >= 0 && to < from;
  });

export function PracticeQuarter({ api, teamId, catalog, onReady, state }: { readonly api: ApiClient; readonly teamId: string; readonly catalog: ContentCatalog; readonly onReady: () => void; readonly state: PracticeState }) {
  const coach = useCoach();
  const { decisions, setDecisions, result, setResult } = state;
  const [error, setError] = useState<ErrorBody["error"] | null>(null);
  const [pending, setPending] = useState(false);
  const ids = PRACTICE_FAMILIES.flatMap((f) => catalog.focusDecisions?.families[f]?.opening ?? []);
  const entries = catalog.decisions.filter((d) => ids.includes(d.decisionId));

  // The practice decisions the player moved off the standing policy, by name.
  const movedEntries = entries.filter((e) => changeTag(e, decisions, STANDING_POLICY) !== null);

  const finish = async () => {
    setPending(true);
    const r = await api.call("practiceRun", { teamId }, { decisions });
    setPending(false);
    if (r.ok) { setResult(r.value); setError(null); } else setError(r.error);
  };

  if (result !== null) {
    return <PracticeResultView result={result} catalog={catalog} twinLine={TWIN_LINE} moved={movedEntries.map((e) => e.label)} onChange={() => setResult(null)} onReady={onReady}
      onAskCoach={() => coach.open({ screen: "chair", label: "Your practice quarter", ask: "Why did my share price move against my twin?", answer: priceAnswer(result, catalog, movedEntries.map((e) => e.label)), explain: { price: priceAnswer(result, catalog, movedEntries.map((e) => e.label)) } })} />;
  }
  return (
    <div className="gx-chair-practice" data-testid="chair.practice">
      {pending ? <GxLoading words="Running your practice quarter…" height={450} testId="chair.practice.running" /> : <GxCard title="Practice quarter" aside={<GxPill tone="info">Not scored, not saved</GxPill>}>
        {entries.length === 0 ? <p className="gx-card-b gx-muted">The practice decisions are unavailable.</p>
          : entries.map((e) => <GxDecisionControl key={e.decisionId} entry={e} card={catalog.conceptCards.find((c) => c.id === e.coachCardIds[0])} decisions={decisions} seed={STANDING_POLICY} access={{ kind: "open" }} stopLabels editedBy={null} context={<div style={{ height: 16 }} />}
              onChange={(patch) => setDecisions((d) => Object.entries(patch).reduce((acc, [path, value]) => withDecisionPath(acc, path, value), d))} />)}
        <div className="gx-card-b gx-chair-ask" data-testid="chair.coach">
          <span className="gx-eyebrow">Ask the Coach</span>
          <div className="gx-chair-chips">
            {practicePrompts(entries, new Set(movedEntries.map((e) => e.decisionId)), new Set(movedEntries.filter((e) => loweredFrom(e, decisions)).map((e) => e.decisionId))).map((l) => <button key={l} type="button" className="gx-chip" data-testid="chair.coach.prompt" onClick={() => coach.open({ screen: "chair", label: "Your practice quarter", ask: l })}>{l}</button>)}
          </div>
        </div>
      </GxCard>}
      {error === null ? null : <div style={{ marginTop: 12 }}><GxErrorBanner error={error} /></div>}
      <GxStickyBar lead={<span className="gx-faint" data-testid="chair.finish.note">Nothing moves until you finish. A change to hiring shows over a year.</span>}>
        <button type="button" className="gx-btn gx-btn--ghost" data-testid="chair.skip" disabled={pending} onClick={onReady}>Skip the practice</button>
        <button type="button" className="gx-btn gx-btn--primary" data-testid="chair.finish" disabled={pending} onClick={() => void finish()}>{pending ? "Running practice…" : "Finish practice"}</button>
      </GxStickyBar>
    </div>
  );
}
